import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import FormButton from "components/form/FormButton";
import TextInput from "components/form/TextInput";
// import DateInput from "components/form/DateInput";
import {
  Box,
  Typography,
  // Checkbox,
  // FormControlLabel,
  // FormControl,
  // Select,
  // MenuItem,
} from "@mui/material";
import cssLayout from "../layout.module.scss";
import css from "./employeesscreen.module.scss";
import { getLanguageString as _ } from "misc/lang";
import { Paths } from "appConfig";
import {
  // apiHrEmployeeCreate,
  apiHrEmployeeUpdate,
  apiHrEmployeeGet,
  apiHrEmployeeDelete,
  apiHrEmployeeInvite,
} from "net/requests";

interface EmployeeFormData {
  account_id: string;
  nickname: string;
  email: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  settings_locked: boolean;
}

const EditEmployeeScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const { account, admin, loader, navigate, globalAppMode } = props;
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const passedAccountId = (location.state as any)?.accountId;
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [formData, setFormData] = useState<EmployeeFormData>({
    account_id: passedAccountId || account?.uuid || "",
    nickname: "",
    email: "",
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    settings_locked: false,
  });

  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();
  const orgName = account?.name || "";
  const title = isEditMode
    ? `${_("employees", "edit_title_prefix")}${orgName}`
    : `${_("employees", "create_title_prefix")}${orgName}`;
  const subtitle = isEditMode
    ? _("employees", "edit_subtitle")
    : _("employees", "create_subtitle");

  const handleDelete = () => setDeleteDialog(true);

  const handleSave = async () => {
    if (!admin) return;

    // Validate required fields
    if (!formData.email) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      loader.setLoading();
      const data = {
        ...formData,
        account_id: passedAccountId || formData.account_id,
      };

      if (isEditMode && id) {
        await apiHrEmployeeUpdate(admin, id, data);
      } else {
        // const { data: userData } = await apiHrEmployeeCreate(admin, data);
        // const { account_id, email } = userData;
        await apiHrEmployeeInvite(admin, {
          account_id: passedAccountId || formData.account_id,
          email: formData.email,
          send_email: true,
          website: window.location.origin,
        });
      }

      loader.reset();
      navigate(Paths.EMPLOYEES);
    } catch (error) {
      loader.reset();
      console.error("Error saving employee:", error);
      alert("Failed to save employee. Please try again.");
    }
  };

  const handleCancel = () => {
    navigate(Paths.EMPLOYEES);
  };

  const confirmDelete = async () => {
    if (!admin || !id) return;
    try {
      loader.setLoading();
      await apiHrEmployeeDelete(admin, id);
      loader.reset();
      setDeleteDialog(false);
      navigate(Paths.EMPLOYEES);
    } catch (error) {
      loader.reset();
      console.error("Error deleting employee:", error);
      setDeleteDialog(false);
    }
  };

  const loadEmployee = async () => {
    if (!isEditMode || !admin || !id) return;

    if (mode === "HR") {
      try {
        loader.setLoading();
        const response = await apiHrEmployeeGet(admin, id);
        const employee = response.data;
        setFormData({
          account_id: employee.account_id || account?.uuid || "",
          nickname: employee.nickname || "",
          email: employee.email || "",
          first_name: employee.first_name || "",
          last_name: employee.last_name || "",
          date_of_birth: employee.date_of_birth || "",
          gender: employee.gender || "",
          settings_locked: employee.settings_locked || false,
        });
        loader.reset();
      } catch (error) {
        loader.reset();
        console.error("Error loading employee:", error);
      }
    }
  };

  useEffect(() => {
    loadEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cssLayout.layout}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        showDeleteButton={isEditMode}
        onDelete={handleDelete}
        deleteButtonText={_("organisation", "delete_button")}
      />

      <div className={css.contentBox}>
        {/* Basic Information Section */}
        <Box className={css.section}>
          <Typography variant="h6" className={css.sectionTitle}>
            {_("employees", "basic_information")}
          </Typography>

          <Box className={css.formGrid}>
            <TextInput
              label={_("employees", "email_label")}
              placeholder={_("employees", "placeholder_email")}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.email}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
            />

            {/*
            <TextInput
              label={_("employees", "first_name_label")}
              placeholder={_("employees", "placeholder_first_name")}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.first_name}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, first_name: value }))
              }
            />

            <TextInput
              label={_("employees", "last_name_label")}
              placeholder={_("employees", "placeholder_last_name")}
              labelOutside={true}
              isRequired={true}
              initialValue={formData.last_name}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, last_name: value }))
              }
            />

            <TextInput
              label={_("employees", "nickname_label")}
              placeholder={_("employees", "placeholder_nickname")}
              labelOutside={true}
              initialValue={formData.nickname}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, nickname: value }))
              }
            />

            <DateInput
              label={_("employees", "date_of_birth_label")}
              placeholder={_("employees", "placeholder_date_of_birth")}
              initialValue={formData.date_of_birth}
              updateValue={(value) =>
                setFormData((prev) => ({ ...prev, date_of_birth: value }))
              }
            />

            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {_("employees", "gender_label")}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, gender: e.target.value }))
                  }
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  sx={{
                    boxShadow: "0px 4px 4px 0px #0000000d",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "8px 15px",
                    marginBottom: "5px",
                    minHeight: "42px",
                    fontSize: "12px",
                    fontWeight: 400,
                    "&:hover": {
                      borderColor: "#b0b0b0",
                    },
                    "&.Mui-focused": {
                      borderColor: "#1976d2",
                    },
                    "& .MuiSelect-select": {
                      padding: "0 !important",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: formData.gender ? "inherit" : "#757575",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          fontSize: "12px",
                          fontWeight: 400,
                        },
                        "& .MuiMenuItem-root.Mui-disabled": {
                          color: "#757575",
                          opacity: 1,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {_("employees", "placeholder_gender")}
                  </MenuItem>
                  <MenuItem value="male">
                    {_("employees", "gender_male")}
                  </MenuItem>
                  <MenuItem value="female">
                    {_("employees", "gender_female")}
                  </MenuItem>
                  <MenuItem value="prefer_not_to_say">
                    {_("employees", "gender_prefer_not_to_say")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.settings_locked}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      settings_locked: e.target.checked,
                    }))
                  }
                />
              }
              label={_("employees", "settings_locked_label")}
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: 400,
                },
              }}
            />
            */}
          </Box>
        </Box>
      </div>

      {/* Action Buttons */}
      <Box className={css.actionButtons}>
        <FormButton
          label={_("general", "btn_save")}
          onSubmit={handleSave}
          inline={true}
          variant="contained"
          color="primary"
        />
        <FormButton
          label={_("general", "btn_cancel")}
          onSubmit={handleCancel}
          inline={true}
          variant="outlined"
        />
      </Box>

      <ConfirmDialog
        open={deleteDialog}
        title={_("general", "btn_delete")}
        message={_("general", "confirm_delete")}
        confirmLabel={_("general", "btn_delete")}
        cancelLabel={_("general", "btn_cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog(false)}
      />
    </div>
  );
};

export default EditEmployeeScreen;
